import { useEffect, useRef, type ReactNode } from "react";

import { cn } from "../lib/cn.js";
import { useFinePointer, useMotionDisabled } from "../lib/motion.js";

/**
 * A surface that catches the light: a soft highlight tracking the pointer, brightest where the
 * pointer is and falling away with distance.
 *
 * IT KNOWS NOTHING ABOUT A CARD. It takes children and a class name, and the only thing it adds is a
 * decorative overlay whose centre follows the pointer. A payment card composes it; so could a panel,
 * a tile, or nothing at all. Putting the effect on the card component instead would have made the
 * card's own tests about `requestAnimationFrame`.
 *
 * ==========================================================================================
 * WHY THE POSITION IS A CUSTOM PROPERTY WRITTEN BY A REF, AND NOT REACT STATE
 * ==========================================================================================
 *
 * Pointer movement produces tens of events a second. Each one, held in state, is a render of this
 * component and everything below it — for a decoration nobody interacts with. Written straight to the
 * node's `style`, the browser recomputes one gradient and React never hears about it.
 *
 * The two properties are `--sheen-x` and `--sheen-y`, in pixels relative to the surface's own box.
 * The gradient in the overlay's inline style reads them, so the whole visual behaviour is one
 * declaration and the JavaScript only ever moves a point.
 *
 * ==========================================================================================
 * ONE FRAME, ONE MEASUREMENT, ONE WRITE
 * ==========================================================================================
 *
 * NFR-015. A pointer handler that writes on every event does layout work per event; one that reads
 * `getBoundingClientRect()` per event also forces a synchronous layout per event, which is the
 * expensive half. So: the handler stores the latest client coordinates and schedules a frame if one
 * is not already pending, and the frame does the measuring and the writing. Ten events between two
 * frames therefore cost one rect read and one style write, not ten of each.
 *
 * The rect is read INSIDE the frame rather than cached on pointer entry, because the surface can move
 * under the pointer — a scroll, a sibling expanding, a responsive reflow — and a cached rect turns
 * that into a highlight sitting at an offset from the cursor with no way to recover.
 *
 * ==========================================================================================
 * `clientX/clientY`, NEVER `offsetX/offsetY`
 * ==========================================================================================
 *
 * `offsetX/offsetY` are relative to the event's TARGET, which is whichever descendant the pointer is
 * over — the name, the amount, a badge. They reset as the pointer crosses each child, so the
 * highlight jumps to the corner of every element it passes over. `clientX` minus the surface's own
 * `left` is relative to one box, whatever the pointer happens to be above.
 *
 * ==========================================================================================
 * THE THREE CONSTRAINTS FROM THE SPEC
 * ==========================================================================================
 *
 * **Reduced motion.** With `useMotionDisabled()` true, no listener is attached AT ALL — not attached
 * and inert. The surface renders a static finish instead, so the treatment is still there and simply
 * does not follow anything. This also covers Playwright, where `navigator.webdriver` is true.
 *
 * **Keyboard and screen reader.** The overlay is `aria-hidden` with `pointer-events: none`: it is
 * announced to nothing and it cannot swallow a click meant for the content beneath it. Everything a
 * consumer wants read out is a child, in real text.
 *
 * **Hover-capable pointers only.** `(hover: hover) and (pointer: fine)`, via `useFinePointer()` — a
 * touch device has no pointer to track, and attaching the handlers there would make the highlight jump
 * to wherever the last tap landed and stay. A phone therefore gets the same STATIC finish reduced
 * motion gets, not a flat rectangle: the two are different reasons for the same outcome, and rendering
 * nothing would make a phone's cards visibly plainer than a laptop's for no stated reason.
 *
 * Both conditions come from hooks subscribed to their media queries rather than from a `matchMedia`
 * call inside the effect. A visitor can turn reduced motion on, or plug a mouse into a tablet, while
 * the page is open; a value read once at mount describes a device they no longer have.
 */

export interface SheenSurfaceProps {
  children: ReactNode;
  /** Classes for the surface itself. It has no size, radius or colour of its own. */
  className?: string;
  /**
   * How far the highlight reaches, in pixels. Larger reads as a broader, softer light.
   *
   * A prop rather than a constant because a payment card and a full-width panel want different
   * answers, and the alternative is every consumer overriding it with a class the effect cannot see.
   */
  radius?: number;
}

export function SheenSurface({ children, className, radius = 320 }: SheenSurfaceProps): ReactNode {
  const surface = useRef<HTMLDivElement | null>(null);
  const motionDisabled = useMotionDisabled();
  const finePointer = useFinePointer();

  /** Whether this visitor gets the tracking version at all. One name, used by the effect and the render. */
  const tracking = !motionDisabled && finePointer;

  useEffect(() => {
    // No listener at all. See the header — this is the requirement, not an optimisation.
    if (!tracking) return;

    const node = surface.current;
    if (!node) return;

    /*
     * The latest pointer position, and whether a frame is already booked.
     *
     * Locals rather than refs: they exist for the lifetime of this effect and nothing renders from
     * them, so a ref would only add indirection and a second thing to reset on cleanup.
     */
    let clientX = 0;
    let clientY = 0;
    let frame = 0;

    const paint = () => {
      frame = 0;
      // Inside the frame, every frame. A cached rect goes stale on any scroll or reflow.
      const rect = node.getBoundingClientRect();
      node.style.setProperty("--sheen-x", `${clientX - rect.left}px`);
      node.style.setProperty("--sheen-y", `${clientY - rect.top}px`);
      node.style.setProperty("--sheen-opacity", "1");
    };

    const onPointerMove = (event: PointerEvent) => {
      // `clientX/clientY`, not `offsetX/offsetY`. See the header.
      clientX = event.clientX;
      clientY = event.clientY;
      // Coalesced: a frame already booked is a frame that will use the coordinates just stored.
      if (frame === 0) frame = window.requestAnimationFrame(paint);
    };

    const onPointerLeave = () => {
      if (frame !== 0) {
        window.cancelAnimationFrame(frame);
        frame = 0;
      }
      /*
       * It SETTLES BACK rather than snapping off. Only the opacity is cleared, so the highlight fades
       * from where it was; resetting the position too would slide it to the top-left corner on the way
       * out, which reads as a glitch rather than as a light going out.
       */
      node.style.setProperty("--sheen-opacity", "0");
    };

    node.addEventListener("pointermove", onPointerMove);
    node.addEventListener("pointerleave", onPointerLeave);

    return () => {
      node.removeEventListener("pointermove", onPointerMove);
      node.removeEventListener("pointerleave", onPointerLeave);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [tracking]);

  return (
    <div ref={surface} className={cn("relative isolate overflow-hidden", className)}>
      {/*
        THE DECORATION. `aria-hidden` and `pointer-events-none`, so it is announced to nothing and
        intercepts nothing — a focusable child inside `children` stays clickable through it.

        Without tracking — reduced motion, or a touch device — this is a fixed diagonal finish: the
        same treatment, not following anything. Rendering nothing instead would make those cards
        visibly flatter than everyone else's, which is a downgrade rather than an accommodation.
      */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-10"
        style={
          tracking
            ? {
                /*
                 * Reads the two custom properties the effect writes. The fallbacks place the
                 * highlight off the surface, so the first paint before any pointer movement is
                 * indistinguishable from the pointer never having arrived.
                 */
                background: `radial-gradient(${radius}px circle at var(--sheen-x, -100%) var(--sheen-y, -100%), rgb(124 58 237 / 0.22), rgb(124 58 237 / 0.06) 42%, transparent 72%)`,
                opacity: "var(--sheen-opacity, 0)",
                // Only the fade is transitioned. Transitioning the POSITION would fight the
                // per-frame writes and lag the highlight behind the cursor by the duration.
                transition: "opacity 220ms ease-out",
              }
            : {
                background:
                  "linear-gradient(115deg, rgb(255 255 255 / 0.14) 0%, rgb(255 255 255 / 0.04) 42%, rgb(255 255 255 / 0) 62%)",
              }
        }
      />
      {children}
    </div>
  );
}
